/**
 * LEMO - Chat Restaurant ↔ Admin
 * Interface de messagerie pour les restaurants
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { 
  Send, 
  Paperclip, 
  Image as ImageIcon, 
  File, 
  X, 
  Loader2,
  MessageCircle,
  Bell
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Son de notification
const playNotificationSound = () => {
  try {
    const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdH2JkI2Coverage6Ojk5OTo+PDw8Pz09PT4+Pj4+Pj09PTw8PDs7Ozs6Ojo5OTk4ODg4NzNTBQUFAwMDAQEBAAAAAAAAAAAAAAAAAAAA');
    audio.volume = 0.5;
    audio.play().catch(() => {});
  } catch (e) {
    // Ignore audio errors
  }
};

export default function RestaurantChat() {
  useAuth();
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const lastMessageCountRef = useRef(0);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Créer ou récupérer la conversation
  const initConversation = useCallback(async () => {
    try {
      // D'abord essayer de récupérer les conversations existantes
      const response = await axios.get(`${API}/chat/conversations`);
      
      if (response.data.length > 0) {
        setConversation(response.data[0]);
        return response.data[0];
      }
      
      // Sinon créer une nouvelle conversation
      const createResponse = await axios.post(`${API}/chat/conversations`, {});
      
      setConversation(createResponse.data);
      return createResponse.data;
    } catch (error) {
      console.error('Error initializing conversation:', error);
      toast.error('Erreur lors de l\'initialisation du chat');
      return null;
    }
  }, []);

  // Charger les messages
  const loadMessages = useCallback(async (conv) => {
    if (!conv) return;
    
    try {
      const response = await axios.get(`${API}/chat/conversations/${conv.id}/messages`);
      
      const newMessages = response.data;
      
      // Vérifier s'il y a de nouveaux messages
      if (newMessages.length > lastMessageCountRef.current && lastMessageCountRef.current > 0) {
        const lastMsg = newMessages[newMessages.length - 1];
        if (lastMsg.sender_role === 'Admin') {
          playNotificationSound();
          toast.info('Nouveau message de l\'administrateur', {
            duration: 3000
          });
        }
      }
      
      lastMessageCountRef.current = newMessages.length;
      setMessages(newMessages);
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  }, []);

  // Initialisation
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      const conv = await initConversation();
      if (conv) {
        await loadMessages(conv);
      }
      setLoading(false);
    };
    
    init();
  }, [initConversation, loadMessages]);

  // Polling pour les nouveaux messages (toutes les 5 secondes)
  useEffect(() => {
    if (!conversation) return;
    
    const interval = setInterval(() => {
      loadMessages(conversation);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [conversation, loadMessages]);

  // Scroll to bottom quand les messages changent
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Upload de fichier
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Vérifier la taille (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 10 Mo)');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const isImage = file.type.startsWith('image/');
      setAttachment({
        url: response.data.url,
        type: isImage ? 'image' : 'file',
        name: file.name
      });
      
      toast.success('Fichier prêt à envoyer');
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Supprimer la pièce jointe
  const removeAttachment = () => {
    setAttachment(null);
  };

  // Envoyer un message
  const sendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim() && !attachment) return;
    if (!conversation) return;

    setSending(true);
    try {
      const payload = {
        content: newMessage.trim(),
        attachment_url: attachment?.url || null,
        attachment_type: attachment?.type || null,
        attachment_name: attachment?.name || null
      };

      await axios.post(
        `${API}/chat/conversations/${conversation.id}/messages`,
        payload
      );

      setNewMessage('');
      setAttachment(null);
      await loadMessages(conversation);
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Chat avec l'administration">
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Chat avec l'administration">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden flex flex-col" style={{ height: 'calc(100vh - 200px)' }}>
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-emerald-500 to-emerald-600">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="font-semibold text-white">Support LEMO</h2>
                <p className="text-sm text-emerald-100">Équipe d'administration</p>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages.length === 0 ? (
              <div className="text-center py-12">
                <MessageCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Aucun message pour le moment</p>
                <p className="text-sm text-gray-400 mt-1">
                  Envoyez un message pour démarrer la conversation
                </p>
              </div>
            ) : (
              messages.map((msg) => {
                const isMe = msg.sender_role === 'Restaurant';
                return (
                  <div
                    key={msg.id}
                    className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[75%] rounded-2xl px-4 py-3 ${
                        isMe
                          ? 'bg-emerald-500 text-white rounded-br-md'
                          : 'bg-white border border-gray-200 text-gray-900 rounded-bl-md'
                      }`}
                    >
                      {!isMe && (
                        <p className="text-xs font-medium text-emerald-600 mb-1">
                          {msg.sender_name}
                        </p>
                      )}
                      
                      {/* Contenu du message */}
                      {msg.content && <p className="whitespace-pre-wrap">{msg.content}</p>}
                      
                      {/* Pièce jointe */}
                      {msg.attachment_url && (
                        <div className="mt-2">
                          {msg.attachment_type === 'image' ? (
                            <a href={msg.attachment_url} target="_blank" rel="noopener noreferrer">
                              <img
                                src={msg.attachment_url}
                                alt="Image"
                                className="max-w-full rounded-lg max-h-60 object-cover"
                              />
                            </a>
                          ) : (
                            <a
                              href={msg.attachment_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`flex items-center gap-2 p-2 rounded-lg ${
                                isMe ? 'bg-emerald-600' : 'bg-gray-100'
                              }`}
                            >
                              <File className="w-4 h-4" />
                              <span className="text-sm truncate">{msg.attachment_name || 'Fichier'}</span>
                            </a>
                          )}
                        </div>
                      )}
                      
                      {/* Heure */}
                      <p className={`text-xs mt-1 ${isMe ? 'text-emerald-100' : 'text-gray-400'}`}>
                        {new Date(msg.created_at).toLocaleString('fr-FR', {
                          hour: '2-digit',
                          minute: '2-digit',
                          day: '2-digit',
                          month: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Zone de saisie */}
          <div className="border-t border-gray-200 p-4 bg-white">
            {/* Aperçu pièce jointe */}
            {attachment && (
              <div className="mb-3 p-3 bg-gray-100 rounded-xl flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {attachment.type === 'image' ? (
                    <ImageIcon className="w-5 h-5 text-emerald-500" />
                  ) : (
                    <File className="w-5 h-5 text-blue-500" />
                  )}
                  <span className="text-sm text-gray-700 truncate max-w-xs">
                    {attachment.name}
                  </span>
                </div>
                <button
                  onClick={removeAttachment}
                  className="p-1 hover:bg-gray-200 rounded-full"
                >
                  <X className="w-4 h-4 text-gray-500" />
                </button>
              </div>
            )}

            <form onSubmit={sendMessage} className="flex items-center gap-2">
              {/* Bouton pièce jointe */}
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileUpload}
                className="hidden"
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="shrink-0"
              >
                {uploading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Paperclip className="w-5 h-5 text-gray-500" />
                )}
              </Button>

              {/* Input message */}
              <Input
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Écrivez votre message..."
                className="flex-1 rounded-full"
                disabled={sending}
              />

              {/* Bouton envoyer */}
              <Button
                type="submit"
                disabled={sending || (!newMessage.trim() && !attachment)}
                className="bg-emerald-500 hover:bg-emerald-600 rounded-full shrink-0"
              >
                {sending ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
